import type { PlatformMarketAccessSource, PlatformMarketWindowSummary } from '@/lib/game/types'

export interface PlatformMarketNavState {
  playMode?: string | null
  platformMarket?: {
    isOpen: boolean
    source: PlatformMarketAccessSource
    reason: string
    currentWindow?: PlatformMarketWindowSummary | null
    nextWindow?: PlatformMarketWindowSummary | null
  } | null
  marketplaceAuctions?: {
    isOpen: boolean
    reason: string
  } | null
}

export interface PlatformMarketNavPresentation {
  label: string
  isDisabled: boolean
  title: string
}

export interface AuctionMarketNavPresentation {
  isVisible: boolean
  label: string
  isDisabled: boolean
  title: string
}

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) return null
  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : null
}

function formatRemainingTime(targetTimestamp: number, now: Date): string | null {
  const diffMs = targetTimestamp - now.getTime()
  if (diffMs <= 0) return null

  const totalMinutes = Math.ceil(diffMs / 60000)
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, '0')}h`
  }

  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`
}

export function formatPlatformMarketNavLabel(
  state: PlatformMarketNavState | null | undefined,
  now: Date = new Date()
): string {
  const platformMarket = state?.platformMarket
  const playMode = state?.playMode?.toLowerCase()

  if (playMode === 'cash') {
    return 'PLATFORM MARKET'
  }

  if (!platformMarket) {
    return 'PLATFORM MARKET'
  }

  if (platformMarket.isOpen) {
    const closesAt = parseTimestamp(platformMarket.currentWindow?.endsAt)
    const remaining = closesAt == null ? null : formatRemainingTime(closesAt, now)
    return remaining ? `PLATFORM MARKET (Closes in ${remaining})` : 'PLATFORM MARKET (Open)'
  }

  const opensAt = parseTimestamp(platformMarket.nextWindow?.startsAt)
  const remaining = opensAt == null ? null : formatRemainingTime(opensAt, now)
  return remaining ? `PLATFORM MARKET (Opens in ${remaining})` : 'PLATFORM MARKET (Closed)'
}

export function getPlatformMarketNavPresentation(
  state: PlatformMarketNavState | null | undefined,
  now: Date = new Date()
): PlatformMarketNavPresentation {
  const platformMarket = state?.platformMarket
  const playMode = state?.playMode?.toLowerCase()

  if (playMode === 'cash') {
    return {
      label: 'PLATFORM MARKET',
      isDisabled: false,
      title: 'Cash Platform Market is always open.'
    }
  }

  if (playMode === 'token' && !platformMarket) {
    return {
      label: 'PLATFORM MARKET (Checking)',
      isDisabled: true,
      title: 'Checking Token Platform Market availability.'
    }
  }

  if (!platformMarket) {
    return {
      label: 'PLATFORM MARKET',
      isDisabled: false,
      title: 'Platform Market'
    }
  }

  if (playMode === 'token' && platformMarket.isOpen) {
    const closesAt = parseTimestamp(platformMarket.currentWindow?.endsAt)
    const remaining = closesAt == null ? null : formatRemainingTime(closesAt, now)
    return {
      label: formatPlatformMarketNavLabel(state, now),
      isDisabled: false,
      title: remaining
        ? `Token Platform Market is open. Closes in ${remaining}.`
        : 'Token Platform Market is open.'
    }
  }

  if (playMode === 'token') {
    const opensAt = parseTimestamp(platformMarket.nextWindow?.startsAt)
    const remaining = opensAt == null ? null : formatRemainingTime(opensAt, now)
    return {
      label: formatPlatformMarketNavLabel(state, now),
      isDisabled: true,
      title: remaining
        ? `Token Platform Market is closed. Opens in ${remaining}.`
        : 'Token Platform Market is closed.'
    }
  }

  return {
    label: formatPlatformMarketNavLabel(state, now),
    isDisabled: false,
    title: 'Platform Market'
  }
}

export function getAuctionMarketNavPresentation(
  state: PlatformMarketNavState | null | undefined
): AuctionMarketNavPresentation {
  if (state?.playMode?.toLowerCase() !== 'token') {
    return {
      isVisible: false,
      label: 'AUCTION MARKET',
      isDisabled: true,
      title: 'Auction Market is available only in Token Play.'
    }
  }

  if (state.marketplaceAuctions === undefined) {
    return {
      isVisible: true,
      label: 'AUCTION MARKET (CHECKING)',
      isDisabled: true,
      title: 'Checking Token Auction Market availability.'
    }
  }

  if (state.marketplaceAuctions === null) {
    return {
      isVisible: true,
      label: 'AUCTION MARKET (UNAVAILABLE)',
      isDisabled: true,
      title: 'Token Auction Market availability is unavailable.'
    }
  }

  if (state.marketplaceAuctions.isOpen) {
    return {
      isVisible: true,
      label: 'AUCTION MARKET',
      isDisabled: false,
      title: state.marketplaceAuctions.reason || 'Token Auction Market is open.'
    }
  }

  return {
    isVisible: true,
    label: 'AUCTION MARKET (CLOSED)',
    isDisabled: true,
    title: state.marketplaceAuctions.reason || 'Token Auction Market is closed.'
  }
}
